import { CircleCheck } from 'lucide-react-native';
import { memo } from 'react';
import { twJoin } from 'tailwind-merge';

import { Button, ButtonIcon, ButtonText } from '@/components/ui/button';
import { GlassSurface, useLiquidGlass } from '@/components/ui/glass';
import { ON_YOUR_HONOR_COPY } from '../../model/onYourHonor';
import { getAccessibilityProps } from '@/util/accessibilityProps';

/**
 * Visual tokens for the outline/unmarked state. Exported so OnYourHonorInlineBadge
 * can derive its appearance from the same source — if these change, the badge
 * changes too without a separate update.
 */
export const ON_YOUR_HONOR_OUTLINE = {
  border: 'border-secondary-400',
  borderHover: 'data-[hover=true]:border-secondary-500',
  text: 'text-secondary-500',
} as const;

/**
 * Label and icon colors per state, shared by both so they cannot drift apart.
 *
 * Every interaction state is pinned on purpose. Gluestack's own `solid` +
 * `secondary` compound variant drives both the label and the icon to
 * `typography-800` on hover and press, which lands near-black on the dark
 * `secondary` fill. Overriding only the resting color leaves those hover rules in
 * force, so the piece that skips them flips dark mid-press while the other stays
 * white. Anything using these must carry all three states or reintroduce that split.
 */
const CONTENT_COLOR = {
  marked:
    'text-typography-white data-[hover=true]:text-typography-white data-[active=true]:text-typography-white dark:text-typography-950 dark:data-[hover=true]:text-typography-950 dark:data-[active=true]:text-typography-950',
  unmarked: `${ON_YOUR_HONOR_OUTLINE.text} data-[hover=true]:text-secondary-600 data-[active=true]:text-secondary-700`,
} as const;

/**
 * Eases the hover and press color shifts on web instead of snapping them.
 *
 * Applied to the pressable and to its label and icon separately, because a CSS
 * transition only covers the element that declares it — one on the button would ease
 * the fill and border while the text jumped.
 */
const COLOR_TRANSITION = 'web:transition-colors web:duration-150';

/**
 * Icon-to-label gap, overriding the `Button` base's `gap-2`.
 *
 * At the `2xs` size the base gap matches the button's own `px-2`, so the glyph sat as
 * far from its label as from the button's edge and read as floating rather than
 * paired with it. Half the padding groups the two into one unit. Exported so the
 * banner's replica pairs them identically — a gap that drifts is as visible as a
 * missing glyph.
 */
export const ON_YOUR_HONOR_ICON_GAP = 'gap-1';

/** The fill and border an unmarked button drops on glass, so only the glass shows. */
const GLASS_UNMARKED_CLASS =
  'border-0 bg-transparent data-[hover=true]:bg-transparent data-[active=true]:bg-transparent';

type OnYourHonorActionProps = {
  isConfirmed: boolean;
  isSending: boolean;
  onPress: () => void;
};

/**
 * Composer "On your honor" toggle, rendered as a design-system `Button` on the
 * secondary (blue) brand ramp: outlined when off, filled when on.
 *
 * The shade choices are pinned to clear WCAG AA in both themes: the secondary
 * ramp inverts between light and dark, so the marked fill can't hold white label
 * text above AA with one shade — it swaps `secondary-600` (light) for
 * `secondary-300` (dark), which resolve to the same mid-slate. The marked state
 * is also exposed via `selected` / `aria-pressed`, never by color alone.
 *
 * The mark is reversible: pressing it while marked removes it. Because the bar
 * floats over the chat, press feedback darkens the fill (light theme: hover
 * `secondary-700`, active `secondary-800`) rather than dimming opacity — a
 * translucent press would let chat text show through the button. Darkening only
 * raises the white label's contrast, so it stays above AA; the dark-theme fill
 * is already opaque and stays static.
 *
 * The same check glyph renders in both states, recolored rather than added. Showing
 * it only when marked widened the button on press, and since the action row is
 * right-aligned the pill jumped leftward out from under the cursor that had just
 * pressed it. One unconditional icon makes that shift impossible by construction.
 *
 * A check on the unmarked state is deliberate and reads as the offer rather than as
 * an accomplished fact, because the button carries the state everywhere else at once:
 * outline versus solid fill, blue-on-transparent versus white-on-blue, plus
 * `selected` / `aria-pressed`. The glyph is the one thing that stays put.
 *
 * Where Liquid Glass is drawn the unmarked button is a glass capsule, matching the
 * floating composer it sits above. It wraps the button in `GlassSurface` rather
 * than taking `Button`'s glass variant, which would put the classes on the capsule
 * and lose the pinned icon gap. Marked stays the solid fill on every platform:
 * glass can only tint with the brand green, and the solid secondary shades are the
 * ones pinned to clear AA, so the state still reads as filled versus clear.
 */
export const OnYourHonorAction = memo(function OnYourHonorAction({
  isConfirmed,
  isSending,
  onPress,
}: OnYourHonorActionProps) {
  const hasGlass = useLiquidGlass();

  const isGlass = hasGlass && !isConfirmed;

  const contentColor = twJoin(COLOR_TRANSITION, CONTENT_COLOR[isConfirmed ? 'marked' : 'unmarked']);

  const unmarkedClass = isGlass
    ? GLASS_UNMARKED_CLASS
    : `bg-background-0 data-[hover=true]:bg-background-50 data-[active=true]:border-secondary-600 data-[active=true]:bg-background-100 ${ON_YOUR_HONOR_OUTLINE.border} ${ON_YOUR_HONOR_OUTLINE.borderHover}`;

  const button = (
    <Button
      size="2xs"
      action="secondary"
      className={twJoin(
        ON_YOUR_HONOR_ICON_GAP,
        COLOR_TRANSITION,
        isConfirmed
          ? 'border-secondary-600 bg-secondary-600 data-[hover=true]:border-secondary-700 data-[hover=true]:bg-secondary-700 data-[active=true]:border-secondary-800 data-[active=true]:bg-secondary-800 dark:border-secondary-300 dark:bg-secondary-300 dark:data-[hover=true]:border-secondary-300 dark:data-[hover=true]:bg-secondary-300 dark:data-[active=true]:border-secondary-300 dark:data-[active=true]:bg-secondary-300'
          : unmarkedClass
      )}
      variant={isConfirmed ? 'solid' : 'outline'}
      onPress={onPress}
      isDisabled={isSending}
      {...getAccessibilityProps({
        label: isConfirmed ? ON_YOUR_HONOR_COPY.confirmedLabel : ON_YOUR_HONOR_COPY.action,
        role: 'button',
        hint: ON_YOUR_HONOR_COPY.helper,
      })}
      aria-pressed={isConfirmed}
      accessibilityState={{ selected: isConfirmed, disabled: isSending }}>
      <ButtonIcon as={CircleCheck} size="xs" className={contentColor} />
      <ButtonText className={twJoin('text-sm', contentColor)}>
        {ON_YOUR_HONOR_COPY.action}
      </ButtonText>
    </Button>
  );

  if (!isGlass) {
    return button;
  }

  return (
    <GlassSurface className="rounded-full" isInteractive={!isSending} isDimmed={isSending}>
      {button}
    </GlassSurface>
  );
});
